/**
 * Tiny JWT payload decoder.
 *
 * The SPA reads claims (specifically `roles`) out of the access token it
 * acquires for the API. We are NOT verifying the signature — the API does
 * that on every request, with the tenant JWKS. The SPA only needs the
 * roles list to decide which UI affordances to render. Misbehaving
 * payloads here have no security impact: the server is the authoriser.
 *
 * Returning `Record<string, unknown>` keeps the consumer responsible for
 * validating the shape of the claim it cares about (e.g. `roles` should
 * be a string[]).
 */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const parts = token.split('.')
  if (parts.length !== 3) return null
  const payload = parts[1]
  if (!payload) return null

  try {
    // Base64URL → base64 → text → JSON. atob handles base64; we patch the
    // URL-safe alphabet (-/_ → +//) and pad to a multiple of 4 first.
    const padded = payload.replace(/-/g, '+').replace(/_/g, '/')
    const padding = padded.length % 4 === 0 ? '' : '='.repeat(4 - (padded.length % 4))
    const decoded = atob(padded + padding)
    const json = decodeURIComponent(
      Array.from(decoded)
        .map(c => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
        .join(''),
    )
    const parsed: unknown = JSON.parse(json)
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>
    }
    return null
  } catch {
    return null
  }
}
